import React, {useState} from "react";
import {
    Container,
    Paper,
    TextField,
    Grid,
    Typography,
    Button,
    Box,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    Link,
    Chip,
    Stack, Alert,
} from '@mui/material';
import {GuestAnswer} from "../../../types/types";
import {EmptyGuestAnswer} from "../../../utils/constants";
import {ATTENDANCE} from "../../../types/enums";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useRibRequest} from "../../../utils/function.utils";

/* ================== TYPES ================== */

type SubmissionStatus = "submitting" | "success" | "error" | null;

/* ================== CONSTANTS ================== */

const PRICE_PER_NIGHT = 30;

/* ================== COMPONENT ================== */

const GuestAnswersForm: React.FC = () => {
    const [guests, setGuests] = useState<GuestAnswer[]>([EmptyGuestAnswer]);
    const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>(null);
    const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);

    const [totalAmount, setTotalAmount] = useState(0);
    const [showPayment, setShowPayment] = useState(false);
    const [showRecap, setShowRecap] = useState(false);

    const { ribRequested, isLoading, requestRib } = useRibRequest();

    const GOOGLE_SHEET_WEB_APP_URL =
        "https://script.google.com/macros/s/AKfycbz_Qf5sEv_LxfYIbCrkMxQoB5WM8ZicELPhf7VNvJSKmCRhpUqty4qoFXez0ENBrrs96g/exec";

    /* ================== HELPERS ================== */

    const calculateTotalAmount = (guests: GuestAnswer[]) =>
        guests.reduce((total, guest) => {
            if (guest.attendance !== "present") return total;

            let nights = 0;
            if (guest.stayToSleepFriday) nights += 1;
            if (guest.stayToSleepSaturday) nights += 1;

            return total + nights * PRICE_PER_NIGHT;
        }, 0);

    const getGuestNights = (guest: GuestAnswer) => {
        let nights = 0;
        if (guest.stayToSleepFriday) nights += 1;
        if (guest.stayToSleepSaturday) nights += 1;
        return nights;
    };

    const getAttendanceChip = (attendance?: ATTENDANCE) => {
        if (attendance === "present") return {label: "Présent(e)", color: "success" as const};
        if (attendance === "absent") return {label: "Absent(e)", color: "error" as const};
        return {label: "Non renseigné", color: "default" as const};
    };



    /* ================== HANDLERS ================== */

    const handleAddGuest = () => setGuests([...guests, EmptyGuestAnswer]);

    const handleRemoveGuest = (index: number) => {
        if (guests.length > 1) {
            setGuests(guests.filter((_, i) => i !== index));
        }
    };

    const handleGuestChange = (
        index: number,
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const {name, value, type} = e.target;
        const newGuests = [...guests];

        newGuests[index] = {
            ...newGuests[index],
            [name]:
                type === "checkbox"
                    ? (e.target as HTMLInputElement).checked
                    : value,
        };

        setGuests(newGuests);
    };

    const handleRadioChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const newGuests = [...guests];
        newGuests[index] = {
            ...newGuests[index],
            attendance: e.target.value as ATTENDANCE,
        };
        setGuests(newGuests);
    };

    const handleEdit = () => {
        setShowRecap(false);
        setShowPayment(false);
        setSubmissionStatus(null);
        setSubmissionMessage(null);
    };

    const handleRequestRib = async () => {
        const guestNames = guests
            .filter(g => g.firstName || g.lastName)
            .map(g => `${g.firstName} ${g.lastName}`)
            .join(', ');

        await requestRib({
            guestNames: guestNames,
            amount: `${totalAmount}`,
            pageOrigin: "RSVP"
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmissionStatus("submitting");
        setSubmissionMessage(null);

        const guestsToSubmit = guests.filter(
            g => g.firstName.trim() || g.lastName.trim()
        );

        if (!guestsToSubmit.length) {
            setSubmissionStatus("error");
            setSubmissionMessage("Veuillez renseigner au moins un invité.");
            return;
        }

        if (guestsToSubmit.some(g => !g.attendance)) {
            setSubmissionStatus("error");
            setSubmissionMessage("Veuillez indiquer la présence pour tous les invités.");
            return;
        }

        const payload = new FormData();
        payload.append("guestsData", JSON.stringify(guestsToSubmit));

        try {
            const response = await fetch(GOOGLE_SHEET_WEB_APP_URL, {
                method: "POST",
                body: payload,
            });

            const result = await response.json();

            if (result.result === "success") {
                const amount = calculateTotalAmount(guestsToSubmit);

                setTotalAmount(amount);
                setShowPayment(amount > 0);
                setShowRecap(true);

                setSubmissionStatus("success");
                setSubmissionMessage(`Merci ! ${result.addedGuests} réponse(s) enregistrée(s).`);
            } else {
                throw new Error(result.message);
            }
        } catch (err: any) {
            setSubmissionStatus("error");
            setSubmissionMessage(err.message || "Erreur inconnue.");
        }
    };

    /* ================== RENDER ================== */

    return (
        <Container component={Paper} elevation={6} sx={{p: 4, borderRadius: 2}}>
            {!showRecap && (<>
                <Typography variant="body1" sx={{color: 'text.secondary', textAlign: 'center'}}>
                    Ce formulaire vous permet de nous transmettre votre réponse au faire-part. Afin de nous permettre d'organiser au mieux la réception, veuillez renseigner les informations demandées
                    pour chaque invité(e). <br/><br/>
                </Typography>
                < Typography variant="body1" sx={{color: 'text.secondary', textAlign: 'center'}}>
                    Nous avons hâte de partager ce beau moment avec vous ! <br/><br/>
                </Typography></>)
            }



            {!showRecap && (
                <>
                    <Alert severity="info" icon={<InfoOutlinedIcon/>} sx={{my: 2}}>
                        Des couchages sont disponibles pour tout le monde au domaine.
                        Une petite participation est
                        demandée pour couvrir les frais. <br/>Toutes les informations sont dans la section <Link
                        href="/stay" color="inherit">Loger</Link> du site.
                    </Alert>
                    <form onSubmit={handleSubmit}>

                        {/* ===== FORM ===== */}
                        {guests.map((guest, index) => (
                            <Box key={index} sx={{mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2}}>
                                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 3}}>
                                    <Typography variant="h6" sx={{color: 'primary.main'}}>Invité(e) {index + 1}</Typography>
                                    {guests.length > 1 && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => handleRemoveGuest(index)}
                                        >
                                            Supprimer
                                        </Button>
                                    )}
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid size={{xs: 12, sm: 4}}>
                                        <TextField label="Prénom" fullWidth required name="firstName"
                                                   value={guest.firstName} onChange={e => handleGuestChange(index, e)}/>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 4}}>
                                        <TextField label="Nom" fullWidth required name="lastName" value={guest.lastName}
                                                   onChange={e => handleGuestChange(index, e)}/>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 4}}>
                                        <FormControl required>
                                            <FormLabel>Présence</FormLabel>
                                            <RadioGroup row value={guest.attendance}
                                                        onChange={e => handleRadioChange(index, e)}>
                                                <FormControlLabel value="present" control={<Radio/>} label="Présent(e)"/>
                                                <FormControlLabel value="absent" control={<Radio/>} label="Absent(e)"/>
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>

                                    {guest.attendance === "present" && (
                                        <>
                                            <Grid size={{xs: 12, sm: 6}}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={guest.stayToSleepFriday}
                                                            onChange={(e) => handleGuestChange(index, e)}
                                                            name="stayToSleepFriday"
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Dormira au domaine le vendredi"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={guest.stayToSleepSaturday}
                                                            onChange={(e) => handleGuestChange(index, e)}
                                                            name="stayToSleepSaturday"
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Dormira au domaine le samedi"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={guest.brunchAttendance}
                                                            onChange={(e) => handleGuestChange(index, e)}
                                                            name="brunchAttendance"
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Sera présent.e au brunch le dimanche"
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12, sm: 6}}>
                                                <TextField
                                                    label="Allergies et régimes alimentaires spécifiques"
                                                    variant="outlined"
                                                    fullWidth
                                                    name="mealPreference"
                                                    value={guest.mealPreference}
                                                    onChange={(e) => handleGuestChange(index, e)}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        ))}

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                flexWrap: "wrap",
                                gap: 2,
                                mt: 4,
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleAddGuest}
                            >
                                + Ajouter un(e) autre invité(e)
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={submissionStatus === 'submitting'}
                            >
                                {submissionStatus === 'submitting'
                                    ? 'Envoi en cours...'
                                    : 'Confirmer'}
                            </Button>
                        </Box>
                    </form>
                </>
            )}

            {/* ===== RECAP ===== */}
            {showRecap && (

                <Box>
                    <Typography variant="h5" textAlign="center" gutterBottom sx={{mb:2}}>
                        Récapitulatif
                    </Typography>
                    <Alert severity="info" icon={<InfoOutlinedIcon/>} sx={{my: 2}}>
                        <strong>Merci pour votre réponse, nous l'avons bien enregistrée ! </strong>
                        <br/>Pour rappel, une petite participation est
                        demandée pour couvrir les frais des couchages au domaine.
                        <br/>Toutes les informations sont dans la section <Link
                        href="/stay" color="inherit">Loger</Link> du site.
                    </Alert>


                    {guests.map((guest, index) => {
                        const nights = getGuestNights(guest);
                        const amount = nights * PRICE_PER_NIGHT;
                        const attendanceChip = getAttendanceChip(guest.attendance);

                        return (
                            <Box key={index} sx={{p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2}}>
                                <Box sx={{display: "flex", gap: 1}}>
                                    <Typography fontWeight={600}>
                                        {guest.firstName} {guest.lastName}
                                    </Typography>
                                    <Chip label={attendanceChip.label} color={attendanceChip.color} size="small" />
                                </Box>

                                {guest.attendance === "present" && (
                                    <>
                                        <Typography>🛏 Nuits : {nights || "Aucune"}</Typography>
                                        {nights > 0 && <Typography>{nights} × 30 € = {amount} €</Typography>}
                                        <Typography>🥐 Brunch : {guest.brunchAttendance ? "Oui" : "Non"}</Typography>
                                        {guest.mealPreference && <Typography>⚠️ {guest.mealPreference}</Typography>}
                                    </>
                                )}
                            </Box>
                        );
                    })}



                    {showPayment ? (<>
                            <Box textAlign='center'>
                                <Typography variant="h6" textAlign="center">
                                    Total : {totalAmount} €
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Choisissez votre moyen de paiement :
                                </Typography>

                                <Stack spacing={2} alignItems="center">
                                    {/* PayPal */}
                                    <Button
                                        variant="contained"
                                        href={`https://paypal.me/ThibaultQuoy/${totalAmount}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            bgcolor: '#0067C5',
                                            borderRadius: '9999px',
                                            textTransform: 'none',
                                            py: 1.25,
                                            px: 3,
                                            minWidth: 220,
                                            alignItems: 'center',
                                            gap: 0.8,
                                            '&:hover': {
                                                bgcolor: '#005a94',
                                            },
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{color: 'white', fontWeight: 500, fontSize: '1rem'}}
                                        >
                                            Payer avec
                                        </Typography>
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontStyle: 'italic',
                                                fontWeight: 'bold',
                                                color: 'white',
                                                fontSize: '1rem',
                                                fontFamily: '"PayPal Bold", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                            }}
                                        >
                                            PayPal
                                        </Typography>
                                    </Button>


                                    {/* Virement / RIB */}
                                    <Button
                                        variant="contained"
                                        color={ribRequested ? "success" : "primary"}
                                        onClick={handleRequestRib}
                                        disabled={ribRequested || isLoading}
                                        sx={{
                                            borderRadius: '9999px',
                                            textTransform: 'none',
                                            py: 1.25,
                                            px: 3,
                                            minWidth: 220,
                                            fontWeight: 500,
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {isLoading ? 'Envoi en cours...' : ribRequested ? 'RIB demandé ✓' : 'Payer par virement'}
                                    </Button>
                                </Stack>

                                {ribRequested && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Nous avons bien reçu votre demande et vous enverrons le RIB par email dès que possible ! <br/> Pour rappel votre réponse au formulaire a bien été enregistrée, merci beaucoup !
                                    </Alert>
                                )}

                            </Box></>
                    ) : (
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                            Aucun hébergement sélectionné — aucun paiement requis.
                        </Typography>
                    )}


                    <Button variant="text" onClick={handleEdit} sx={{mt: 2, display: "block", mx: "auto"}}>
                        Modifier ma réponse
                    </Button>
                </Box>
            )}
        </Container>
    );
};

export default GuestAnswersForm;